//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//
import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '../../ui/collapsible';
import { Spinner } from '../../ui/spinner';
import { Button, buttonVariants } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../ui/tooltip';
import { cn } from '../../../lib/utils';
import ToolCallDetails from './ToolCallDetails';
import { TOOL_CARD_BORDER_CLASS, TOOL_CARD_BG_CLASS } from './toolCardStyle';
import { pickForToolStatus } from './turnGrouping';
import type {
  HitlApprovalScope,
  ToolResultBlock,
  ToolOutputRef,
  ToolStatus,
} from '../../../types/streaming';

export interface ToolCallBoxProps {
  toolName: string;
  toolArgs: Record<string, unknown>;
  toolResult?: string;
  toolStatus: ToolStatus;
  resultBlocks?: ToolResultBlock[];
  // Spec 007: the output was too large to keep as text (size message + Download).
  outputRef?: ToolOutputRef;
  toolCallId?: string;
  onApprove?: (scope: HitlApprovalScope) => void;
  onReject?: () => void;
  approvalDisabled?: boolean;
  /**
   * True when a Task is active (Task mode) — offers "this task"/"this
   * session" grant scopes. False/omitted means Query mode execution phase —
   * offers "this plan execution"/"this session" instead.
   */
  taskActive?: boolean;
}

interface ToolApprovalActionsProps {
  onApprove?: (scope: HitlApprovalScope) => void;
  onReject?: () => void;
  disabled?: boolean;
  /**
   * True when a Task is active (Task mode) — offers "this task"/"this
   * session" grant scopes. False/omitted means Query mode execution phase —
   * offers "this plan execution"/"this session" instead.
   */
  taskActive?: boolean;
}

// The pending-approval action row: what scopes are offered and how they're
// labeled is approval policy, not tool-call rendering, so it's kept out of
// the generic ToolCallBox body (mirrors PlanApprovalCard living outside
// ChatThread's generic message rendering for the analogous plan approval).
const ToolApprovalActions: React.FC<ToolApprovalActionsProps> = ({
  onApprove,
  onReject,
  disabled = false,
  taskActive = false,
}) => {
  const extraScopeOption: { scope: HitlApprovalScope; label: string } =
    taskActive
      ? { scope: 'task', label: 'Auto-approve this tool for this task' }
      : {
          scope: 'plan_execution',
          label: 'Auto-approve this tool until the plan finishes',
        };

  return (
    <div className="mt-2 flex justify-end gap-2">
      <div className="flex">
        <TooltipProvider>
          <Tooltip>
            {/* Styled directly on TooltipTrigger (no nested Button/asChild)
                — Radix's Slot merge does not compose cleanly with this
                repo's installed dependency graph, producing a duplicate
                wrapping <button>. */}
            <TooltipTrigger
              className={cn(
                buttonVariants({ variant: 'default', size: 'sm' }),
                'rounded-r-none',
              )}
              onClick={() => onApprove?.('once')}
              disabled={disabled}
            >
              Approve
            </TooltipTrigger>
            <TooltipContent>this request only</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="default"
              size="sm"
              aria-label="More approval options"
              className="rounded-l-none border-l border-primary-foreground/20 px-1.5"
              disabled={disabled}
            >
              <ChevronDown className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem
              onClick={() => onApprove?.(extraScopeOption.scope)}
            >
              {extraScopeOption.label}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onApprove?.('session')}>
              Auto-approve this tool for this conversation
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="border-red-600 text-red-600 hover:bg-red-50 dark:border-red-400 dark:text-red-400 dark:hover:bg-red-900/20"
        onClick={() => onReject?.()}
        disabled={disabled}
      >
        Reject
      </Button>
    </div>
  );
};

// Muted grey wrench (mirrored so the open jaw sits top-left)
const WrenchIcon: React.FC = () => (
  <span className="inline-flex shrink-0 items-center" aria-hidden="true">
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#94A3B8"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transform: 'scaleX(-1)' }}
    >
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  </span>
);

const ToolCallBox: React.FC<ToolCallBoxProps> = ({
  toolName,
  toolArgs,
  toolResult,
  toolStatus,
  resultBlocks,
  outputRef,
  toolCallId,
  onApprove,
  onReject,
  approvalDisabled = false,
  taskActive = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // A pending approval is always shown expanded — there is nothing to
  // collapse to yet, and the user needs to see the action row immediately.
  const open = toolStatus === 'pending_approval' || isOpen;

  const statusIcon = pickForToolStatus(toolStatus, {
    running: <Spinner size="sm" className="shrink-0 text-violet-600" />,
    pending_approval: (
      <span
        className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.3px] text-amber-700"
        aria-label="needs approval"
      >
        Needs approval
      </span>
    ),
    success: (
      <span
        className="shrink-0 text-[14px] leading-none text-green-600"
        aria-label="success"
      >
        ✓
      </span>
    ),
    error: (
      <span
        className="shrink-0 text-[13px] leading-none text-red-600"
        aria-label="error"
      >
        ✗
      </span>
    ),
  });

  return (
    <Collapsible
      open={open}
      onOpenChange={setIsOpen}
      className={cn(
        'mb-2 overflow-hidden rounded-lg border font-[inherit] text-[12px]',
        TOOL_CARD_BG_CLASS,
        toolStatus === 'pending_approval'
          ? 'border-amber-300'
          : TOOL_CARD_BORDER_CLASS,
      )}
    >
      <CollapsibleTrigger
        className="flex w-full cursor-pointer select-none items-center gap-2 border-none bg-transparent px-3 py-2 text-left text-[#1C262E] [&[data-state=open]>svg]:rotate-0"
        aria-label={toolName}
        disabled={toolStatus === 'pending_approval'}
      >
        <WrenchIcon />
        <span className="flex-1 text-[13px] font-semibold text-violet-800">
          {toolName}
        </span>
        {statusIcon}
        {toolStatus !== 'pending_approval' && (
          <ChevronUp
            className="size-3.5 shrink-0 rotate-180 text-slate-500 transition-transform duration-[280ms]"
            aria-hidden="true"
          />
        )}
      </CollapsibleTrigger>

      <CollapsibleContent className={cn('border-t', TOOL_CARD_BORDER_CLASS)}>
        <div className="px-3 pb-2.5">
          <ToolCallDetails
            toolArgs={toolArgs}
            toolResult={toolResult}
            resultBlocks={resultBlocks}
            toolStatus={toolStatus}
            outputRef={outputRef}
            toolCallId={toolCallId}
          />
          {toolStatus === 'pending_approval' && (
            <ToolApprovalActions
              onApprove={onApprove}
              onReject={onReject}
              disabled={approvalDisabled}
              taskActive={taskActive}
            />
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default ToolCallBox;
